import { action, computed, makeObservable, observable, runInAction, toJS } from "mobx";
import {
  ApiClient,
  CollectionName,
  GenomesBulkDownloadPayload,
  GenomesOrderBy,
  RestCallResult,
  GenomeStarred,
  GenomesBulkDownloadQueryResult,
  GenomeInList,
} from "../api";
import {
  getDefaultSearchObject,
  SearchFilterKeys,
  SearchFilterProps,
  SearchFilters,
  SearchFilterValue,
} from "../utils/search";
import UserStore from "./user-store";

const DEFAULT_ORDER_BY: GenomesOrderBy = "taxon.name";

const FALSY_VALUES = ["0", "false", "f", "no", "n"];

export type BulkPayload = Omit<GenomesBulkDownloadPayload, "search_object"> & {
  search_object: SearchFilters;
};

const normalizeBooleanString = (value: string | null): string | null => {
  return value === null ? null : (!FALSY_VALUES.includes(value.toLowerCase())).toString();
};

/**
 * Make a single Genome an observable with only the `starred` attribute being observed.
 * @param rawGenome a plain genome object returned from the API.
 */
export const observedGenome = (rawGenome: GenomeInList): GenomeInList => {
  return observable(rawGenome, { starred: observable }, { deep: false });
};

export default abstract class BaseGenomeStore {
  public api: ApiClient;
  public userStore: UserStore;

  @observable public collectionName: CollectionName | null = null;
  @observable public hydrated = false;

  @observable orderBy: GenomesOrderBy = DEFAULT_ORDER_BY;
  @observable ascending = true;
  @observable searchObject = getDefaultSearchObject();

  @observable mineOnly = false;

  @observable protected selectionUuids = observable.set<string>([]);
  @observable protected selectAllActivated = false;

  protected constructor(api: ApiClient, userStore: UserStore, ssrData?: BaseGenomeStore) {
    makeObservable(this);
    this.api = api;
    this.userStore = userStore;

    if (ssrData) {
      this.hydrated = ssrData.hydrated;

      runInAction(() => {
        for (const key of Object.keys(ssrData.searchObject) as SearchFilterKeys[]) {
          if (key === "tags") {
            this.searchObject.tags = observable.array(ssrData.searchObject.tags);
          } else {
            const k = key as Exclude<SearchFilterKeys, "tags">;
            this.searchObject[k] = ssrData.searchObject[k];
          }
        }
        this.mineOnly = ssrData.mineOnly;
        this.collectionName = ssrData.collectionName;
        this.orderBy = ssrData.orderBy;
        this.ascending = ssrData.ascending;
      });
    }
  }

  @computed
  get urlParams(): URLSearchParams {
    const params = new URLSearchParams();
    if (this.orderBy !== this.defaultOrderBy) {
      params.set("orderBy", this.orderBy);
    }

    if (this.ascending !== this.defaultAscending(this.orderBy)) {
      params.set("ascending", this.ascending ? "true" : "false");
    }

    // Pages are 1-indexed
    const page = this.pageNum || 1;
    if (page > 1) {
      params.set("page", page.toString(10));
    }

    // Put search args at root level of the url
    for (const section of this.searchSections) {
      const val = this.searchObject[section.key];
      if (!val) {
        continue;
      }
      if (section.key === "tags") {
        if ((val as Array<string>).length > 0) {
          params.set(section.key, (val as Array<string>).join(","));
        }
      } else if (section.key === "strain_name") {
        params.set("strain_alias", val as string);
      } else {
        params.set(section.key, val as string);
      }
    }
    this.addAdditionalUrlParams(params);
    return params;
  }

  protected addAdditionalUrlParams(_params: URLSearchParams): void {
    // Do nothing
  }

  @action
  resetSearchFilters = (): void => {
    const defaultSearch = getDefaultSearchObject();
    for (const key of Object.keys(defaultSearch) as SearchFilterKeys[]) {
      if (key === "tags") {
        this.searchObject.tags.replace(defaultSearch.tags.slice());
      } else {
        const k = key as Exclude<SearchFilterKeys, "tags">;
        this.searchObject[k] = defaultSearch[k];
      }
    }
  };

  @computed
  get inUseSearchFields(): SearchFilterProps[] {
    const searchFields = [];

    for (const section of this.searchSections) {
      const val = this.searchObject[section.key];
      if (val === null || val === undefined) {
        continue;
      }

      if (section.key === "tags") {
        for (const tag of val) {
          searchFields.push({ title: section.title, key: section.key, val: tag });
        }
      } else {
        searchFields.push({ title: section.title, key: section.key, val: val });
      }
    }

    return searchFields;
  }

  @action
  toggleSelected = (genomeUuid: string) => {
    if (this.selectionUuids.has(genomeUuid)) {
      this.selectionUuids.delete(genomeUuid);
    } else {
      this.selectionUuids.add(genomeUuid);
    }
  };

  @action
  deselectAll = () => {
    this.selectionUuids.clear();
    this.selectAllActivated = false;
  };

  @action
  selectAll = () => {
    this.selectionUuids.clear();
    this.selectAllActivated = true;
  };

  @action
  togglePageSelected = (genomeUuids: string[]) => {
    const isPageSelected = this.isPageSelected(genomeUuids);
    let callback =
      (!this.selectAllActivated && isPageSelected) || (this.selectAllActivated && !isPageSelected)
        ? this.selectionUuids.delete
        : this.selectionUuids.add;

    callback = callback.bind(this.selectionUuids);
    for (const uuid of genomeUuids) {
      callback(uuid);
    }
  };

  @action
  setMineOnly = (mineOnly: boolean) => {
    this.deselectAll();
    this.mineOnly = mineOnly;
    this.setPage(1);
  };

  @action.bound
  toggleGenomeStarring = async (starred: boolean, uuid: string): Promise<void> => {
    const res = await this.api.patch<GenomeStarred>(
      "/genome-portal-api/genomes/{uuid}",
      { starred },
      { uuid }
    );
    runInAction(() => {
      if (res.error === undefined) {
        this.setGenomeStarring(uuid, res.data.starred);
      }
    });
  };

  isItemSelected = (genomeUuid: string): boolean => {
    if (this.selectAllActivated) {
      return !this.selectionUuids.has(genomeUuid);
    } else {
      return this.selectionUuids.has(genomeUuid);
    }
  };

  isPageSelected = (genomeUuids: string[]): boolean => {
    if (genomeUuids.length === 0) {
      return false;
    }
    for (const uuid of genomeUuids) {
      if (!this.isItemSelected(uuid)) {
        return false;
      }
    }
    return true;
  };

  protected baseSetOrderBy = (orderBy: GenomesOrderBy) => {
    if (orderBy !== this.orderBy) {
      this.ascending = this.defaultAscending(orderBy);
    } else {
      this.ascending = !this.ascending;
    }
    this.orderBy = orderBy;
  };

  protected baseSetVariablesFromQuery = (params: { [key: string]: unknown }) => {
    if (Object.keys(params).length === 0) {
      this.searchObject = getDefaultSearchObject();
    }

    for (const section of this.searchSections) {
      const val = params[section.key];
      if (!val) {
        continue;
      }

      if (section.key === "tags") {
        for (const tag of (val as string).split(",")) {
          this.searchObject.tags.push(tag);
        }
      } else if (section.key === "type_strain") {
        this.searchObject[section.key] = normalizeBooleanString(val as string);
      } else if (typeof val === "string") {
        this.searchObject[section.key] = val;
      }
    }

    const strainAlias = params["strain_alias"];
    if (strainAlias && typeof strainAlias === "string") {
      this.searchObject["strain_name"] = strainAlias;
    }

    let orderBy = params["orderBy"];
    if (!orderBy) {
      orderBy = this.defaultOrderBy;
    }
    let ascending = params["ascending"];
    if (!ascending) {
      ascending = this.defaultAscending(orderBy as GenomesOrderBy) ? "true" : "false";
    }
    this.orderBy = orderBy as GenomesOrderBy;
    this.ascending = ascending === "true";
  };

  protected baseSearchBulkDownload = async (
    downloadTypes: GenomesBulkDownloadPayload["download_types"],
    additionalParams: Partial<BulkPayload>
  ): Promise<RestCallResult<GenomesBulkDownloadQueryResult>> => {
    const bulkPayload: BulkPayload = {
      ...additionalParams,
      download_types: downloadTypes,
      genome_uuids: [...this.selectionUuids.values()],
      query_type: !this.selectAllActivated ? "inclusion" : "exclusion",
      search_object: toJS(this.searchObject),
      collection_name: this.collectionName ? [this.collectionName] : this.defaultCollectionNames,
      mine_only: this.mineOnly,
    };
    return await this.api.post<GenomesBulkDownloadQueryResult>(
      "/genome-portal-api/genomes/bulk-file-search",
      bulkPayload
    );
  };

  protected baseInitBulkDownload = async (
    downloadTypes: GenomesBulkDownloadPayload["download_types"],
    additionalParams: Partial<BulkPayload>
  ): Promise<RestCallResult<unknown>> => {
    const bulkPayload: BulkPayload = {
      ...additionalParams,
      download_types: downloadTypes,
      genome_uuids: [...this.selectionUuids.values()],
      query_type: !this.selectAllActivated ? "inclusion" : "exclusion",
      search_object: toJS(this.searchObject),
      collection_name: this.collectionName ? [this.collectionName] : this.defaultCollectionNames,
      mine_only: this.mineOnly,
    };
    return await this.api.post<unknown>(
      "/genome-portal-api/genomes/bulk-file-download",
      bulkPayload
    );
  };

  initSingleGenomeSearch = async (
    genomeUuid: string,
    downloadTypes: GenomesBulkDownloadPayload["download_types"]
  ): Promise<RestCallResult<GenomesBulkDownloadQueryResult>> => {
    const bulkPayload: BulkPayload = {
      genome_uuids: [genomeUuid],
      query_type: "inclusion",
      download_types: downloadTypes,
      collection_name: this.collectionName ? [this.collectionName] : this.defaultCollectionNames,
      search_object: getDefaultSearchObject(),
    };
    if (this.userStore.lotId) {
      bulkPayload.lot_id = this.userStore.lotId;
    }
    return await this.api.post<GenomesBulkDownloadQueryResult>(
      "/genome-portal-api/genomes/bulk-file-search",
      bulkPayload
    );
  };

  initSingleGenomeDownload = async (
    genomeUuid: string,
    downloadTypes: GenomesBulkDownloadPayload["download_types"]
  ): Promise<RestCallResult<unknown>> => {
    const bulkPayload: BulkPayload = {
      genome_uuids: [genomeUuid],
      query_type: "inclusion",
      download_types: downloadTypes,
      collection_name: this.collectionName ? [this.collectionName] : this.defaultCollectionNames,
      search_object: getDefaultSearchObject(),
    };
    if (this.userStore.lotId) {
      bulkPayload.lot_id = this.userStore.lotId;
    }
    return await this.api.post<unknown>(
      "/genome-portal-api/genomes/bulk-file-download",
      bulkPayload
    );
  };

  @action.bound
  addSearchFilter<K extends keyof SearchFilters>(key: K, val: string): void {
    if (key === "tags") {
      if (this.searchObject.tags.indexOf(val) === -1) {
        this.searchObject.tags.push(val);
      }
    } else if (key === "type_strain") {
      this.searchObject.type_strain = normalizeBooleanString(val as string);
    } else {
      this.searchObject[key] = val as SearchFilters[K];
    }
  }

  @action.bound
  removeSearchFilter(key: SearchFilterKeys, val: SearchFilterValue): void {
    if (key === "tags") {
      if (val) {
        this.searchObject.tags.remove(val as string);
      }
    } else {
      this.searchObject[key] = null;
    }
  }

  get defaultOrderBy(): GenomesOrderBy {
    return DEFAULT_ORDER_BY;
  }

  get descendingFirstColumns(): GenomesOrderBy[] {
    return ["created_at"];
  }

  defaultAscending(orderBy: GenomesOrderBy): boolean {
    return !this.descendingFirstColumns.includes(orderBy);
  }

  abstract get pageNum(): number | undefined;

  abstract get searchSections(): SearchFilterProps[];

  abstract get defaultCollectionNames(): CollectionName[];

  abstract setPage(newPage: number): void;

  protected abstract setGenomeStarring(uuid: string, starred: boolean): void;
}
